import React, { useEffect, useState } from "react";
import { Table, Button, Space, Tag, Switch, Modal, message, Image, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import utilityBlockApi from "../../../api/utilityBlockApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faLink } from "@fortawesome/free-solid-svg-icons";
import { getUtilityIcon } from "../../../shared/utility-icon-map";
import { audienceColumn } from "../../../components/audience-fields";
import CenterTabConfigCard from "./CenterTabConfigCard";
import HomeGuideConfigCard from "./HomeGuideConfigCard";

const API_IMG_URL = process.env.REACT_APP_API_URL?.replace("/api/v1", "") || "";

const OPEN_MODE_LABELS = {
    appview: { text: "App view", color: "purple" },
    webview: { text: "Web view", color: "blue" },
    external: { text: "Trình duyệt", color: "red" },
    screen: { text: "Màn hình app", color: "green" },
};

function UtilityBlockList() {
    const navigate = useNavigate();
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const fetchBlocks = async () => {
        try {
            setLoading(true);
            const { data } = await utilityBlockApi.getAll();
            setBlocks(data.data || []);
        } catch {
            message.error("Không thể tải danh sách tiện ích");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchBlocks(); }, []);

    const handleToggle = async (id) => {
        try {
            await utilityBlockApi.toggle(id);
            message.success("Đã cập nhật trạng thái");
            fetchBlocks();
        } catch {
            message.error("Không thể cập nhật trạng thái");
        }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Xác nhận xoá",
            content: "Bạn có chắc muốn xoá tiện ích này?",
            okText: "Xoá", cancelText: "Huỷ", okType: "danger",
            onOk: async () => {
                try {
                    await utilityBlockApi.delete(id);
                    message.success("Đã xoá tiện ích");
                    fetchBlocks();
                } catch {
                    message.error("Không thể xoá tiện ích");
                }
            },
        });
    };

    const handleBulkDelete = () => {
        Modal.confirm({
            title: `Xoá ${selectedRowKeys.length} tiện ích đã chọn?`,
            okText: "Xoá", cancelText: "Huỷ", okType: "danger",
            onOk: async () => {
                try {
                    await utilityBlockApi.bulkDelete(selectedRowKeys);
                    message.success("Đã xoá các tiện ích đã chọn");
                    setSelectedRowKeys([]);
                    fetchBlocks();
                } catch {
                    message.error("Không thể xoá");
                }
            },
        });
    };

    const columns = [
        { title: "ID", dataIndex: "id", key: "id", width: 55 },
        {
            title: "Icon", key: "icon", width: 80,
            render: (_, r) =>
                r.imagePath ? (
                    <Image src={`${API_IMG_URL}${r.imagePath}`} alt="" width={40} height={40}
                        style={{ objectFit: "cover", borderRadius: 8 }} />
                ) : (
                    <span
                        title={r.iconName || ""}
                        style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 40, height: 40, borderRadius: 8,
                            background: r.iconBg || "#f3f4f6", color: r.iconColor || "#374151",
                        }}
                    >
                        <FontAwesomeIcon icon={getUtilityIcon(r.iconName) || faLink} style={{ fontSize: 16 }} />
                    </span>
                ),
        },
        {
            title: "Tên", dataIndex: "title", key: "title", ellipsis: true,
            render: (title, r) => (
                <span>
                    {title}
                    {r.badge ? <Tag color="red" className="ml-2">{r.badge}</Tag> : null}
                </span>
            ),
        },
        {
            title: "Mở kiểu", dataIndex: "openMode", key: "openMode", width: 110,
            render: (m) => {
                const i = OPEN_MODE_LABELS[m] || { text: m, color: "default" };
                return <Tag color={i.color}>{i.text}</Tag>;
            },
        },
        {
            title: "userId", dataIndex: "injectUserId", key: "injectUserId", width: 80, align: "center",
            render: (v) => (v ? <Tag color="gold">Gắn</Tag> : <span className="text-content-3">—</span>),
        },
        audienceColumn(),
        {
            title: "Link / Màn hình", dataIndex: "link", key: "link", ellipsis: true, width: 200,
            render: (url, r) =>
                r.openMode === "screen" ? (
                    <Tag color="green">Màn: {r.screenName || "—"}</Tag>
                ) : (
                    <span className="text-xs text-content-3">{url || "—"}</span>
                ),
        },
        {
            title: "Ưu tiên", dataIndex: "priority", key: "priority", width: 110, align: "center",
            sorter: (a, b) => a.priority - b.priority, defaultSortOrder: "ascend",
            onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
        },
        {
            title: "Lượt bấm", dataIndex: "clicks", key: "clicks", width: 100, align: "center",
            sorter: (a, b) => (a.clicks || 0) - (b.clicks || 0),
            onHeaderCell: () => ({ style: { whiteSpace: "nowrap" } }),
            render: (c) => c || 0,
        },
        {
            title: "Active", dataIndex: "isActive", key: "isActive", width: 75, align: "center",
            render: (v, r) => <Switch checked={v} onChange={() => handleToggle(r.id)} size="small" />,
        },
        {
            title: "Hành động", key: "action", width: 90, fixed: "right",
            render: (_, r) => (
                <Space size={4}>
                    <Tooltip title="Sửa">
                        <Button size="small" icon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() => navigate(`/utility-block/${r.id}/edit`)} />
                    </Tooltip>
                    <Tooltip title="Xoá">
                        <Button size="small" danger icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => handleDelete(r.id)} />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    return (
        <div className="flex-1 p-6 overflow-auto">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
                <h1 className="text-2xl font-bold">Tiện ích App (trang chủ)</h1>
                <Space>
                    {selectedRowKeys.length > 0 && (
                        <Button danger onClick={handleBulkDelete}>
                            Xoá {selectedRowKeys.length} mục
                        </Button>
                    )}
                    <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} className="mr-1" />}
                        onClick={() => navigate("/utility-block/create")}>
                        Tạo mới
                    </Button>
                </Space>
            </div>

            {/* Cấu hình nút giữa thanh tab app + nút hướng dẫn trang chủ — cùng nhóm
                "giao diện app" (chung file /img/app-tab-config.json) nên gộp vào đây
                thay vì mở thêm mục menu riêng. */}
            <CenterTabConfigCard />
            <HomeGuideConfigCard />

            <Table
                columns={columns}
                dataSource={blocks}
                loading={loading}
                rowKey="id"
                rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
                pagination={{ pageSize: 20 }}
                scroll={{ x: "max-content" }}
                size="middle"
            />
        </div>
    );
}

export default UtilityBlockList;
